import type { FormInstance } from 'antd';
import { DatePicker, Form, Input, InputNumber, Radio, Select } from 'antd';
import locale from 'antd/es/date-picker/locale/ja_JP';
import { Rule } from 'antd/es/form';
import CalendarEventFill from 'assets/icons/calendar2-event-fill.svg';
import ChevronDownIcon from 'assets/icons/chevron-down.svg';
import dayjs from 'dayjs';
import 'moment/locale/ja';
import { COMMON } from 'utilities/constants/COMMON';
import { DATE_FORMAT } from 'utilities/constants/DATE';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';

export const InputText = (props: {
    label: string;
    name: string;
    isRequired: boolean;
    requiredMessage?: string;
    rules?: Rule[];
    placeholder?: string;
    form?: FormInstance<any>;
    addonAfter?: string;
    maxLength?: number;
}) => {
    const {
        maxLength,
        addonAfter,
        label,
        name,
        isRequired,
        requiredMessage,
        rules = [],
        placeholder = '',
        form,
    } = props;

    return (
        <section style={{ width: '100%', marginTop: 24 }} className="input-number-custom">
            <div style={{ marginBottom: 16 }} className="label-form">
                {label}
                {isRequired ? <span style={{ color: '#EB5757' }}>*</span> : null}
            </div>
            <Form.Item
                required={false}
                className="input-font-style label-form-style "
                name={name}
                label={false}
                rules={rules.concat([
                    {
                        required: isRequired ? true : false,
                        message: requiredMessage ?? '',
                    },
                ])}
            >
                <Input
                    maxLength={maxLength}
                    addonAfter={addonAfter}
                    autoComplete="off"
                    placeholder={placeholder}
                    size="middle"
                    style={{ width: '100%' }}
                    onChange={(e) => {
                        commonHelpers.preventInputJapanese(e, form, name);
                    }}
                    onBlur={() => {
                        if (form) commonHelpers.onBlurTrimFormItem(form, name);
                    }}
                />
            </Form.Item>
        </section>
    );
};

export const InputDatePicker = (props: {
    label: string;
    name: string;
    isRequired: boolean;
    requiredMessage?: string;
    rules?: Rule[];
    placeholder?: string;
}) => {
    const { label, name, isRequired, requiredMessage, rules = [], placeholder = '' } = props;

    return (
        <section style={{ width: '100%', marginTop: 24 }} className="input-number-custom">
            <div style={{ marginBottom: 16 }} className="label-form">
                {label}
                {isRequired ? <span style={{ color: '#EB5757' }}>*</span> : null}
            </div>
            <Form.Item
                required={false}
                className="input-font-style label-form-style "
                name={name}
                label={false}
                rules={rules.concat([
                    {
                        required: isRequired ? true : false,
                        message: requiredMessage ?? getMessage('Sel_Required', label),
                    },
                ])}
            >
                <DatePicker
                    format={DATE_FORMAT.FORMAT1}
                    getPopupContainer={(trigger: any) => trigger.parentNode}
                    inputReadOnly
                    locale={locale}
                    suffixIcon={<img alt="" src={CalendarEventFill} />}
                    autoComplete="off"
                    placeholder={placeholder}
                    style={{ width: '100%' }}
                    disabledDate={(current) => {
                        const currentDate = new Date();
                        const currentDateJs = dayjs(currentDate);
                        // 3 month after
                        const threeMonthAfter = currentDateJs.add(3, 'month');
                        // 1 year after
                        const oneYearAfter = currentDateJs.add(1, 'year');
                        return current && (current < threeMonthAfter || current > oneYearAfter);
                    }}
                />
            </Form.Item>
        </section>
    );
};
export const InputWholeNumber = (props: {
    label: string;
    name: string;
    isRequired: boolean;
    min?: number;
    max?: number;
    requiredMessage?: string;
    addonAfter?: string;
    rules?: Rule[];
    placeholder?: string;
    maxLength?: number;
}) => {
    const {
        label,
        name,
        isRequired,
        min = 0,
        max = 9999999999,
        requiredMessage,
        addonAfter,
        rules = [],
        placeholder = '',
        maxLength,
    } = props;

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const charCode = event.which ? event.which : event.keyCode;
        const charStr = String.fromCharCode(charCode);
        if (!/^\d+$/.test(charStr)) {
            event.preventDefault();
        }
    };

    const handleChange = (value: any) => {
        if (value === null || value === undefined) {
            return;
        }
        if (typeof value === 'number') {
            value = value.toString();
        }
        const convertedValue = value.replace(/[０-９]/g, (s: string) =>
            String.fromCharCode(s.charCodeAt(0) - 65248)
        );
        if (!/^\d*$/.test(convertedValue)) {
            return;
        }
    };

    return (
        <section style={{ width: '100%', marginTop: 24 }} className="input-number-custom">
            <div style={{ marginBottom: 16 }} className="label-form">
                {label}
                {isRequired ? <span style={{ color: '#EB5757' }}>*</span> : null}
            </div>
            <Form.Item
                required={false}
                className="input-font-style label-form-style "
                name={name}
                label={false}
                rules={rules.concat([
                    {
                        required: isRequired ? true : false,
                        message: requiredMessage ?? '',
                    },
                ])}
            >
                <InputNumber
                    autoComplete="off"
                    onKeyPress={handleKeyPress}
                    onChange={handleChange}
                    controls={false}
                    maxLength={
                        ((max ?? COMMON.MAX_NUMBER_VALUE) + '').replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ',',
                        ).length
                    }
                    max={max}
                    min={min}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    placeholder={placeholder}
                    parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                    size="middle"
                    style={{ width: '100%' }}
                    addonAfter={addonAfter}
                />
            </Form.Item>
        </section>
    );
};

export const InputNumberWithFraction = (props: {
    label: string;
    name: string;
    isRequired: boolean;
    min?: number;
    max?: number;
    requiredMessage?: string;
    labelClassName?: string;
    addonAfter?: string;
    rules?: Rule[];
    placeholder?: string;
    disabled?: boolean;
}) => {
    const {
        label,
        name,
        isRequired,
        labelClassName,
        min = 0,
        max = 9999999999,
        requiredMessage,
        addonAfter,
        rules = [],
        placeholder = '',
        disabled = false,
    } = props;
    return (
        <section className="input-number-custom">
            <div className={labelClassName}>
                {label}
                {isRequired ? <span style={{ color: '#EB5757' }}>*</span> : null}
            </div>
            <Form.Item
                required={false}
                className="input-font-style label-form-style "
                name={name}
                label={false}
                rules={rules.concat([
                    {
                        required: isRequired ? true : false,
                        message: requiredMessage ?? '',
                        validator: (_, value) => {
                            // if (isRequired && (value == 0 || !value)) {
                            //     return Promise.reject();
                            // } else {
                            //     return Promise.resolve();
                            // }
                            // console.log(value? true : false)
                            if (isRequired) {
                                if (value || value == 0) {
                                    return Promise.resolve();
                                } else {
                                    return Promise.reject();
                                }
                            } else {
                                return Promise.resolve();
                            }
                        },
                    },
                ])}
            >
                <InputNumber
                    onKeyPress={(event) =>
                        commonHelpers.typeFractionWithMaxLength(event, (max + '').length)
                    }
                    controls={false}
                    disabled={disabled}
                    max={max}
                    min={min}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    placeholder={placeholder}
                    parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                    size="middle"
                    style={{ width: '100%' }}
                    addonAfter={addonAfter}
                    precision={2}
                    step={0.1}
                />
            </Form.Item>
        </section>
    );
};

export const InputSelectOption = (props: {
    options: { name: string | number; value: string | number }[];
    label: any;
    name: string;
    isRequired: boolean;
    requiredMessage?: string;
    addonAfter?: string;
    rules?: Rule[];
    placeholder?: string;
    disabled?: boolean;
}) => {
    const {
        disabled,
        label,
        name,
        isRequired,
        requiredMessage,
        rules = [],
        placeholder = '',
        options = [],
    } = props;
    return (
        <section style={{ width: '100%', marginTop: 24 }} className="input-number-custom">
            <div style={{ marginBottom: 16 }} className="label-form">
                {label}
                {isRequired ? <span style={{ color: '#EB5757' }}>*</span> : null}
            </div>
            <Form.Item
                required={false}
                className="input-font-style label-form-style  "
                name={name}
                label={false}
                rules={rules.concat([
                    {
                        required: isRequired ? true : false,
                        message: requiredMessage ?? '',
                    },
                ])}
            >
                <Select
                    disabled={disabled ?? false}
                    className="select-custom"
                    size="large"
                    placeholder={placeholder}
                    suffixIcon={<img alt="" src={ChevronDownIcon} />}
                >
                    {options.map((element) => (
                        <Select.Option
                            className="option-style"
                            key={element.value}
                            value={element.value}
                        >
                            {element.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        </section>
    );
};

export const InputRadioOption = (props: {
    options: { name: string | number; value: string | number }[];
    label: string;
    name: string;
    isRequired: boolean;
    requiredMessage?: string;
    addonAfter?: string;
    rules?: Rule[];
    placeholder?: string;
    disabled?: boolean;
}) => {
    const { label, name, isRequired = false, requiredMessage, rules = [], options = [] } = props;
    return (
        <section style={{ width: '100%', marginTop: 24 }} className="input-number-custom">
            <div style={{ marginBottom: 12 }} className="label-form">
                {label}
                {isRequired ? <span style={{ color: '#EB5757' }}>*</span> : <></>}
            </div>
            <Form.Item
                required={false}
                className="input-font-style label-form-style radio-custom-2"
                name={name}
                label={false}
                rules={rules.concat([
                    {
                        required: isRequired ? true : false,
                        message: requiredMessage ?? '',
                    },
                ])}
            >
                <Radio.Group
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        padding: 6,
                    }}
                >
                    {options.map((element) => {
                        return (
                            <Radio key={element.value} style={{ margin: 10 }} value={element.value}>
                                {element.name}
                            </Radio>
                        );
                    })}
                </Radio.Group>
            </Form.Item>
        </section>
    );
};
